import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import api from "../../../axiosConfig";
import MenuContextualTraslado from "./MenuContextualTraslado";
import PaginacionTrasladosAnt from "./PaginacionTrasladosAnt";
import FiltrosTraslados from "./FiltrosTraslados";
import ItemTraslado from "./ItemTraslado";
import TrasladoDialog from "./TrasladoDialog";
import { Spin, Typography, Button, Modal, DatePicker, message, Checkbox } from "antd";
import { LoadingOutlined, DownloadOutlined } from "@ant-design/icons";
import { getWeekRange, getMonthRange } from "./dateUtils";
import { TRANSFER_STATUSES } from "../../../constants/trasladosStatuses";
import useNativeWebSocket from "../../../hooks/useNativeWebSocket";
import dayjs from "../../../utils/dayjs";
import { useUser } from "../../../UserContext";
import { geoJsonToLatLng } from "../../../utils/coordinates";
import { coincideBusquedaTraslado } from "../../../utils/busquedaTraslado";


const { RangePicker } = DatePicker;


/*
 * Función passesAllFilters: verifica que un traslado cumpla con todos los filtros
 */
function passesAllFilters(
  traslado,
  {
    dateFilterType,
    startDate,
    endDate,
    selectedDate,
    selectedSeguimientoStatus,
    selectedConfirmacionStatus,
    selectedZona,
    selectedStatuses,
    selectedMovil,
    searchQuery,
    selectedCopago,
    selectedEps,
  }
) {
  // "fecha" llega como "2026-10-26T05:00:00.000000Z" (medianoche de Colombia en UTC): se usa solo AAAA-MM-DD.
  // Antes se le concatenaba "T00:00:00", la fecha quedaba inválida y este filtro dejaba pasar traslados de
  // cualquier día (2026-10-28: el listado mostraba traslados de una fecha consultada antes).
  const [anio, mes, dia] = String(traslado.fecha || "").slice(0, 10).split("-").map(Number);
  const [hh, mm] = String(traslado.hora_recogida || "00:00").split(":");
  const trasladoDateTime = new Date(anio, mes - 1, dia, parseInt(hh, 10) || 0, parseInt(mm, 10) || 0);
  if (Number.isNaN(trasladoDateTime.getTime())) {
    return false;
  }

  let startDateFilter = new Date(selectedDate);
  let endDateFilter = new Date(selectedDate);

  switch (dateFilterType) {
    case "week":
      ({ start: startDateFilter, end: endDateFilter } = getWeekRange(0));
      break;
    case "previousWeek":
      ({ start: startDateFilter, end: endDateFilter } = getWeekRange(1));
      break;
    case "month":
      ({ start: startDateFilter, end: endDateFilter } = getMonthRange(0));
      break;
    case "previousMonth":
      ({ start: startDateFilter, end: endDateFilter } = getMonthRange(1));
      break;
    case "custom":
      if (startDate) {
        startDateFilter = new Date(startDate);
        startDateFilter.setHours(0, 0, 0, 0);
      }
      if (endDate) {
        endDateFilter = new Date(endDate);
        endDateFilter.setHours(23, 59, 59, 999);
      }
      break;
    case "none":
    default:
      startDateFilter = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        0,
        0,
        0
      );
      endDateFilter = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        23,
        59,
        59
      );
      break;
  }

  if (trasladoDateTime < startDateFilter || trasladoDateTime > endDateFilter) {
    return false;
  }

  if (
    selectedSeguimientoStatus !== "Ambos" &&
    traslado.seguimiento !== selectedSeguimientoStatus
  ) {
    return false;
  }
  if (
    selectedConfirmacionStatus !== "Ambos" &&
    traslado.confirmacion !== selectedConfirmacionStatus
  ) {
    return false;
  }

  if (selectedZona && selectedZona !== "") {
    if (traslado.zona_paciente !== selectedZona) {
      return false;
    }
  }

  if (selectedStatuses.length > 0) {
    if (!selectedStatuses.includes(traslado.status)) {
      return false;
    }
  }

  if (selectedMovil) {
    if (traslado.vehiculo_id !== selectedMovil.id) {
      return false;
    }
  }

  if (!coincideBusquedaTraslado(traslado, searchQuery)) {
    return false;
  }

  // Filtro por copago
  if (selectedCopago && selectedCopago.length > 0) {
    const copagoDiario = Number(traslado.copago_diario) || 0;
    const hasCopago = copagoDiario > 0;

    if (selectedCopago.length === 1) {
      // Si solo hay una opción seleccionada
      if (selectedCopago[0] === "con_copago" && !hasCopago) {
        return false;
      }
      if (selectedCopago[0] === "sin_copago" && hasCopago) {
        return false;
      }
    }
  }
  // Filtro por EPS
  if (selectedEps && selectedEps !== "") {
    if (traslado.empresa !== selectedEps) {
      return false;
    }
  }

  return true;
}

function ListaTraslados({
  openDialog,
  handleOpenDialog,
  handleCloseDialog,
  handleProgramarTraslado,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  selectedZona,
  setSelectedZona,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  selectedCopago,
  onCopagoFilter,
  exportModalVisible,
  setExportModalVisible,
  exportLoading,
  exportDateRange,
  setExportDateRange,
  handleExportTraslados,
  onExport,
  regimenes,
}) {
  // Estados de data y filtros
  const { user: currentUser } = useUser();
  const [traslados, setTraslados] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf("day").toDate());
  const [zonas, setZonas] = useState([]);
  const [zonasPermitidas, setZonasPermitidas] = useState(null); // null = sin restricción, array = zonas permitidas
  const [dateFilterType, setDateFilterType] = useState("none");
  const [singleHourFilter, setSingleHourFilter] = useState(null);
  const [selectedMovil, setSelectedMovil] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTraslado, setSelectedTraslado] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState(TRANSFER_STATUSES);
  const [selectedSeguimientoStatus, setSelectedSeguimientoStatus] =
    useState("Ambos");
  const [selectedConfirmacionStatus, setSelectedConfirmacionStatus] =
    useState("Ambos");
  const [selectedEps, setSelectedEps] = useState("");
  // Estado local de copago para evitar doble fetch por prop asíncrono del padre
  const [localCopago, setLocalCopago] = useState(selectedCopago);

  // Estado de observaciones removido - ahora viene del backend optimizado

  // Estado para almacenar el total absoluto sin filtros
  const [totalAbsoluto, setTotalAbsoluto] = useState(0);

  // Función de permisos para el módulo "Centro de Control"
  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find(
        (m) => m.key === "Centro de Control"
      );
      if (!module || module.estado !== "disponible") return false;

      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Centro de Control" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  // Función fetchObservacionesCounts removida - ya no necesaria
  // El backend ahora incluye observaciones_count optimizado en cada traslado

  // Solo la última consulta actualiza el listado; la anterior se cancela al iniciar otra (cambio de fecha,
  // filtros o recarga automática). Sin esto una respuesta vieja podía quedar en pantalla (2026-10-28).
  const ultimaConsultaRef = useRef(0);
  const controladorConsultaRef = useRef(null);

  // Función para obtener traslados con paginación y manejo de errores mejorado
  const fetchTraslados = useCallback(
    async (params, showLoadingIndicator = true, source = "manual") => {
      const consulta = ++ultimaConsultaRef.current;
      controladorConsultaRef.current?.abort();
      const controlador = new AbortController();
      controladorConsultaRef.current = controlador;
      try {

        if (showLoadingIndicator) {
          setIsLoading(true);
        }

        // Validar rango de fechas antes de enviar
        if (params.fechaInicio && params.fechaFin) {
          const inicio = new Date(params.fechaInicio);
          const fin = new Date(params.fechaFin);
          const diffDays = (fin - inicio) / (1000 * 60 * 60 * 24);

          if (diffDays > 365) {
            message.warning(
              "El rango de fechas es muy grande. Se limitará a 1 año."
            );
            const maxEndDate = new Date(inicio);
            maxEndDate.setDate(maxEndDate.getDate() + 365);
            // Usar formato local para evitar problemas de zona horaria
            const year = maxEndDate.getFullYear();
            const month = String(maxEndDate.getMonth() + 1).padStart(2, '0');
            const day = String(maxEndDate.getDate()).padStart(2, '0');
            params.fechaFin = `${year}-${month}-${day}`;
          }
        }

        // Agregar parámetros de paginación por defecto
        const requestParams = {
          limit: 999999, // Sin límite
          offset: 0,
          ...params,
        };


        const response = await api.get("/traslados", {
          params: requestParams,
          timeout: 120000, // 2 minutos específico para esta consulta
          signal: controlador.signal,
        });
        if (consulta !== ultimaConsultaRef.current) {
          return;
        }

        // Validar que response.data tenga la estructura esperada
        const responseData = response.data || {};
        const {
          rows = [],
          pagination,
          totalCount = 0,
          totalAbsoluto: responseTotalAbsoluto = 0,
          searchApplied = false,
          searchLimit = 999999,
          queryType = "unknown",
        } = responseData;

        const trasladosConRegimen = rows || [];

        // Sin startTransition: React podía dejar en pantalla el listado anterior
        setTraslados(trasladosConRegimen);
        setTotalAbsoluto(responseTotalAbsoluto);

        // Programar notificaciones con setTimeout simple
        if (source === "manual") {
          const scheduleNotifications = (callback) => {
            if (window.scheduler?.postTask) {
              window.scheduler.postTask(callback, { priority: "background" });
            } else {
              setTimeout(callback, 100); // Timeout más pequeño para evitar violations
            }
          };

          scheduleNotifications(() => {
            // Usar requestAnimationFrame adicional para dividir aún más el trabajo
            requestAnimationFrame(() => {
              if (pagination && pagination.hasMore) {
                const currentLimit = searchLimit || 999999;
                const currentCount = Math.min(totalCount, currentLimit);

                if (totalCount > currentLimit) {
                  if (queryType === "date-only") {
                    message.info(
                      `📅 Navegación por fechas: mostrando los primeros ${currentCount} de ${totalCount} traslados. Usa filtros adicionales para buscar registros específicos.`,
                      6
                    );
                  } else {
                    message.warning(
                      `⚡ Búsqueda intensiva: encontrados ${totalCount} traslados, mostrando ${currentCount}. Refina los filtros para resultados más precisos.`,
                      8
                    );
                  }
                }
              } else if (searchApplied && totalCount > 0) {
                // Mensaje de éxito para búsquedas específicas completas
                message.success(
                  `✅ Filtros aplicados: encontrados ${totalCount} traslado${
                    totalCount > 1 ? "s" : ""
                  } que ${
                    totalCount > 1 ? "coinciden" : "coincide"
                  } con los criterios.`,
                  4
                );
              } else if (queryType === "date-only" && totalCount > 0) {
                // Mensaje informativo para navegación por fechas
              }
            });
          });
        } else {
          // Para refresh automático
        }

      } catch (error) {
        // Consulta reemplazada por una más reciente: no es un error
        if (error.code === "ERR_CANCELED" || consulta !== ultimaConsultaRef.current) {
          return;
        }
        console.error("Error al cargar los traslados:", error);

        // Usar setTimeout simple para manejo de errores
        const scheduleErrorHandling = (callback) => {
          if (window.scheduler?.postTask) {
            window.scheduler.postTask(callback, { priority: "background" });
          } else {
            setTimeout(callback, 100); // Timeout más pequeño para evitar violations
          }
        };

        scheduleErrorHandling(() => {
          // Usar requestAnimationFrame para división de trabajo adicional
          requestAnimationFrame(() => {
            if (
              error.response?.status === 408 ||
              error.code === "ECONNABORTED"
            ) {
              message.error({
                content:
                  "La consulta está tomando mucho tiempo. Intenta con un rango de fechas más pequeño.",
                duration: 8,
              });
            } else if (
              error.response?.status === 400 &&
              error.response?.data?.code === "QUERY_TIMEOUT"
            ) {
              message.error({
                content: error.response.data.error,
                duration: 6,
              });
            } else if (
              error.response?.status === 502 ||
              error.response?.status === 503
            ) {
              message.error({
                content:
                  "El servidor está temporalmente no disponible. Intenta de nuevo en unos minutos.",
                duration: 6,
              });
            } else {
              message.error({
                content:
                  "Error al cargar los traslados. Por favor, intente nuevamente.",
                duration: 4,
              });
            }
          });
        });
      } finally {
        // La última consulta apaga el indicador aunque lo haya encendido una anterior que se canceló
        if (consulta === ultimaConsultaRef.current) {
          setIsLoading(false);
        }
      }
    },
    // Sin dependencias: antes dependía de isInitialLoad (sin otro uso) y al terminar la primera carga se
    // creaba una función nueva que repetía la consulta
    []
  );

  // Calcular rango de fechas según filtro
  const obtenerRangoFechas = useCallback(() => {
    let startDateFilter = new Date(selectedDate);
    let endDateFilter = new Date(selectedDate);

    switch (dateFilterType) {
      case "week":
        ({ start: startDateFilter, end: endDateFilter } = getWeekRange(0));
        break;
      case "previousWeek":
        ({ start: startDateFilter, end: endDateFilter } = getWeekRange(1));
        break;
      case "month":
        ({ start: startDateFilter, end: endDateFilter } = getMonthRange(0));
        break;
      case "previousMonth":
        ({ start: startDateFilter, end: endDateFilter } = getMonthRange(1));
        break;
      case "custom":
        if (startDate) startDateFilter = startDate;
        if (endDate) endDateFilter = endDate;
        break;
      case "none":
      default:
        startDateFilter = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate()
        );
        endDateFilter = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate()
        );
        break;
    }

    // Usar formato local para evitar problemas de zona horaria con toISOString()
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDateFilter: formatLocalDate(startDateFilter),
      endDateFilter: formatLocalDate(endDateFilter),
    };
  }, [dateFilterType, selectedDate, startDate, endDate]);

  // Función de refresh para tiempo real que construye parámetros automáticamente (throttled)
  const refreshTraslados = useCallback(() => {
    if (!hasPermission("VISUALIZAR")) return;

    // Usar setTimeout simple para programar el refresh
    const scheduleRefresh = (callback) => {
      if (window.scheduler?.postTask) {
        window.scheduler.postTask(callback, { priority: "background" });
      } else {
        setTimeout(callback, 50); // Timeout más pequeño para evitar violations
      }
    };

    scheduleRefresh(() => {
      const { startDateFilter, endDateFilter } = obtenerRangoFechas();
      const queryParams = {
        zona: selectedZona || "",
        fechaInicio: startDateFilter,
        fechaFin: endDateFilter,
        status: selectedStatuses.length > 0 ? selectedStatuses : [],
        searchQuery: searchQuery || "",
        seguimientoStatus: selectedSeguimientoStatus,
        confirmacionStatus: selectedConfirmacionStatus,
        movil: selectedMovil ? selectedMovil.id : null,
        copago: localCopago,
        eps: selectedEps || "",
      };

      // Configurar filtros de tiempo
      if (startTime && endTime) {
        queryParams.startTime = startTime.toTimeString().slice(0, 5); // HH:mm
        queryParams.endTime = endTime.toTimeString().slice(0, 5); // HH:mm
      }


      fetchTraslados(queryParams, false, "auto-refresh"); // Marcar como refresh automático
    });
  }, [
    hasPermission,
    obtenerRangoFechas,
    selectedZona,
    selectedStatuses,
    startTime,
    endTime,
    searchQuery,
    selectedSeguimientoStatus,
    selectedConfirmacionStatus,
    selectedMovil,
    localCopago,
    fetchTraslados,
    selectedEps,
  ]);


  // Preparar filtros para el hook WebSocket
  const filtrosWebSocket = useMemo(() => ({
    selectedZona,
    selectedStatuses,
    selectedValidacion: selectedSeguimientoStatus,
    selectedConfirmacion: selectedConfirmacionStatus,
    selectedCopago: localCopago,
    selectedVehicleId: selectedMovil?.id || null,
    selectedDepartamento: null, // No disponible en Centro de Control
    selectedCiudad: null, // No disponible en Centro de Control
    selectedHour: singleHourFilter,
    searchTermTraslados: searchQuery,
    zonasPermitidas // Lista de nombres de zonas permitidas para el usuario
  }), [
    selectedZona,
    selectedStatuses,
    selectedSeguimientoStatus,
    selectedConfirmacionStatus,
    localCopago,
    selectedMovil?.id,
    singleHourFilter,
    searchQuery,
    zonasPermitidas
  ]);

  // Hook NATIVO para WebSocket - sin Laravel Echo con validación de fechas y filtros
  const { startDateFilter, endDateFilter } = obtenerRangoFechas();
  const fechaInicioWebSocket = dayjs(startDateFilter);
  const fechaFinWebSocket = dayjs(endDateFilter);
  useNativeWebSocket(traslados, setTraslados, fechaInicioWebSocket, fechaFinWebSocket, filtrosWebSocket);

  // 2) Cargar zonas permitidas por el rol del usuario
  useEffect(() => {
    const fetchZonas = async () => {
      try {
        const response = await api.get("/traslados/zonas-disponibles");
        setZonas(response.data.zonas || []);
        // Guardar los nombres de las zonas permitidas para filtrar WebSocket
        setZonasPermitidas(response.data.zonas_nombres || null);
      } catch (error) {
        console.error("Error al cargar las zonas:", error);
        setZonas([]);
      }
    };
    fetchZonas();
  }, []);

  // Consolidar filtros en un hash para evitar consultas duplicadas
  const filtersHash = useMemo(() => {
    // Generar filtros independientemente de permisos (el permiso se valida en fetchTraslados)
    const { startDateFilter, endDateFilter } = obtenerRangoFechas();
    const filters = {
      zona: selectedZona || "",
      fechaInicio: startDateFilter,
      fechaFin: endDateFilter,
      status: selectedStatuses.length > 0 ? selectedStatuses : [],
      searchQuery: searchQuery || "",
      seguimientoStatus: selectedSeguimientoStatus,
      confirmacionStatus: selectedConfirmacionStatus,
      movil: selectedMovil ? selectedMovil.id : null,
      copago: localCopago,
      eps: selectedEps,
    };

    // Configurar filtros de tiempo
    if (startTime && endTime) {
      filters.startTime = startTime.toTimeString().slice(0, 5); // HH:mm
      filters.endTime = endTime.toTimeString().slice(0, 5); // HH:mm
    } else {
      filters.startTime = null;
      filters.endTime = null;
    }

    return JSON.stringify(filters);
  }, [
    selectedZona,
    selectedStatuses,
    startTime,
    endTime,
    searchQuery,
    selectedSeguimientoStatus,
    selectedConfirmacionStatus,
    selectedMovil,
    localCopago,
    obtenerRangoFechas,
    selectedEps,
  ]);

  // Cargar datos cuando cambien los filtros
  useEffect(() => {
    if (!filtersHash) return;

    const queryParams = JSON.parse(filtersHash);
    fetchTraslados(queryParams, true, "filter-change");
  }, [filtersHash, fetchTraslados]);

  // Función para manejar cambios de filtros (solo actualiza estados)
  const handleFilterChange = useCallback(
    async (filters) => {
      // Solo actualizar estados locales, el useEffect principal se encargará de la consulta
      setSelectedZona(filters.selectedZona);
      setSelectedStatuses(filters.selectedStatuses || []);
      setSelectedSeguimientoStatus(filters.selectedSeguimientoStatus);
      setSelectedConfirmacionStatus(filters.selectedConfirmacionStatus);
      setSelectedMovil(filters.selectedMovil);
      // Solo actualizar copago si viene explícitamente en los filtros
      // El debounced filtersState de FiltrosTraslados no incluye copago,
      // así evitamos que lo sobrescriba con undefined
      if (filters.selectedCopago !== undefined) {
        setLocalCopago(filters.selectedCopago);
        if (onCopagoFilter) onCopagoFilter(filters.selectedCopago);
      }
      setSearchQuery(filters.searchQuery || "");

      // Actualizar estados de fecha/tiempo si existen
      if (filters.singleHourFilter) {
        // Para filtro de hora exacta, convertir a startTime y endTime iguales
        const hourString = filters.singleHourFilter.toTimeString().slice(0, 5); // HH:mm
        const hourDate = new Date();
        const [hours, minutes] = hourString.split(':');
        hourDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        setSingleHourFilter(filters.singleHourFilter);
        setStartTime(hourDate);
        setEndTime(hourDate);
      } else {
        setSingleHourFilter(null);
        // Solo actualizar startTime y endTime si no hay singleHourFilter
        if (filters.startTime) setStartTime(new Date(filters.startTime));
        else setStartTime(null);
        if (filters.endTime) setEndTime(new Date(filters.endTime));
        else setEndTime(null);
      }
      setSelectedEps(filters.selectedEps || "");
    },
    // Las funciones setState son estables por defecto en React
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCopagoFilter]
  );

  // 5) Control de día anterior/siguiente
  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  // 6) Actualizar estado de la lista
  const handleEstadoCambiado = useCallback((id, nuevoValor, campo) => {
    setTraslados((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [campo]: nuevoValor } : t))
    );
  }, []);

  // 7) Menú contextual
  const handleContextMenu = useCallback(
    (event, traslado) => {
      event.preventDefault();
      setSelectedTraslado(traslado);
      setContextMenu(
        contextMenu === null
          ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
          : null
      );
    },
    [contextMenu]
  );

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleCopyToClipboard = useCallback(
    (phone) => {
      navigator.clipboard.writeText(phone);
      handleCloseContextMenu();
    },
    [handleCloseContextMenu]
  );

  const handleOptionClick = useCallback(
    (option) => {
      if (!selectedTraslado) return;
      if (option === "copyPhone") {
        handleCopyToClipboard(selectedTraslado.numero_contacto_paciente);
      } else if (option === "copyResponsiblePhone") {
        // Compatibilidad con el campo legacy
        handleCopyToClipboard(selectedTraslado.numero_contacto_responsable);
      } else if (option.startsWith("copyResponsiblePhone:")) {
        // Nuevo formato: copyResponsiblePhone:PHONE_NUMBER
        const phone = option.split(":")[1];
        handleCopyToClipboard(phone);
      }
    },
    [handleCopyToClipboard, selectedTraslado]
  );

  // Función para ordenar traslados por fecha y hora
  const sortTraslados = (trasladosArray) => {
    return [...trasladosArray].sort((a, b) => {
      // Primero por fecha
      const fechaA = a.fecha || '';
      const fechaB = b.fecha || '';
      if (fechaA !== fechaB) {
        return fechaA.localeCompare(fechaB);
      }
      // Luego por hora de recogida
      const horaA = a.hora_recogida || '';
      const horaB = b.hora_recogida || '';
      if (horaA !== horaB) {
        return horaA.localeCompare(horaB);
      }
      // Finalmente por ID descendente
      return (b.id || 0) - (a.id || 0);
    });
  };

  // 8) Filtrado en memoria optimizado con throttling para evitar recálculos frecuentes
  const trasladosFiltrados = useMemo(() => {
    // Para listas grandes, usar Web Workers o dividir el trabajo
    if (traslados.length > 1000) {
      // División del trabajo en chunks más pequeños para evitar bloqueo del hilo principal
      const chunkSize = 200;
      const chunks = [];
      for (let i = 0; i < traslados.length; i += chunkSize) {
        chunks.push(traslados.slice(i, i + chunkSize));
      }

      const filteredResults = [];
      chunks.forEach((chunk) => {
        const filtered = chunk.filter((t) =>
          passesAllFilters(t, {
            dateFilterType,
            startDate,
            endDate,
            selectedDate,
            startTime,
            endTime,
            singleHourFilter,
            selectedSeguimientoStatus,
            selectedConfirmacionStatus,
            selectedZona,
            selectedStatuses,
            selectedMovil,
            searchQuery,
            selectedCopago: localCopago,
            selectedEps,
          })
        );
        filteredResults.push(...filtered);
      });

      // Ordenar por fecha y hora después de filtrar
      return sortTraslados(filteredResults);
    }

    // Para listas más pequeñas, filtrado normal
    const filtered = traslados.filter((t) =>
      passesAllFilters(t, {
        dateFilterType,
        startDate,
        endDate,
        selectedDate,
        startTime,
        endTime,
        singleHourFilter,
        selectedSeguimientoStatus,
        selectedConfirmacionStatus,
        selectedZona,
        selectedStatuses,
        selectedMovil,
        searchQuery,
        selectedCopago: localCopago,
        selectedEps,
      })
    );

    // Ordenar por fecha y hora después de filtrar
    return sortTraslados(filtered);
  }, [
    traslados,
    dateFilterType,
    startDate,
    endDate,
    selectedDate,
    startTime,
    endTime,
    singleHourFilter,
    selectedSeguimientoStatus,
    selectedConfirmacionStatus,
    selectedZona,
    selectedStatuses,
    selectedMovil,
    searchQuery,
    localCopago,
    selectedEps,
  ]);

  // 9) Paginación en memoria optimizada con caching
  // Selección para "Actualizar origen - destino": casilla por traslado y "seleccionar visibles" (página actual)
  const [seleccionados, setSeleccionados] = useState([]);
  const puedeActualizarOrigenDestino = hasPermission("ACTUALIZAR_ORIGEN_DESTINO_CC");
  const toggleSeleccion = useCallback((id) => {
    setSeleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  // Quitar de la selección los traslados que ya no están en la lista (filtros, recarga)
  useEffect(() => {
    setSeleccionados((prev) => {
      if (prev.length === 0) return prev;
      const ids = new Set(traslados.map((t) => t.id));
      const vigentes = prev.filter((id) => ids.has(id));
      return vigentes.length === prev.length ? prev : vigentes;
    });
  }, [traslados]);
  const handleOrigenDestinoActualizado = useCallback(() => {
    setSeleccionados([]);
    refreshTraslados();
  }, [refreshTraslados]);

  const trasladosPaginados = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;

    // Optimización: si la lista es muy grande, usar virtualización básica
    if (trasladosFiltrados.length > 5000) {
      // Solo procesar los elementos visibles y unos pocos de buffer
      const bufferSize = 50;
      const bufferStart = Math.max(0, start - bufferSize);
      const bufferEnd = Math.min(trasladosFiltrados.length, end + bufferSize);

      return trasladosFiltrados
        .slice(bufferStart, bufferEnd)
        .slice(start - bufferStart, start - bufferStart + rowsPerPage);
    }

    // Para listas normales, slice directo
    return trasladosFiltrados.slice(start, end);
  }, [trasladosFiltrados, page, rowsPerPage]);

  return (
    <>
      <FiltrosTraslados
        dateFilterType={dateFilterType}
        setDateFilterType={setDateFilterType}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        handlePrevDay={handlePrevDay}
        handleNextDay={handleNextDay}
        singleHourFilter={singleHourFilter}
        setSingleHourFilter={setSingleHourFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        startTime={startTime}
        setStartTime={setStartTime}
        endTime={endTime}
        setEndTime={setEndTime}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        zonas={zonas}
        selectedZona={selectedZona}
        setSelectedZona={setSelectedZona}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        selectedSeguimientoStatus={selectedSeguimientoStatus}
        setSelectedSeguimientoStatus={setSelectedSeguimientoStatus}
        selectedConfirmacionStatus={selectedConfirmacionStatus}
        setSelectedConfirmacionStatus={setSelectedConfirmacionStatus}
        selectedMovil={selectedMovil}
        setSelectedMovil={setSelectedMovil}
        handleOpenDialog={
          hasPermission("PROGRAMAR_CC") ? handleOpenDialog : undefined
        }
        handleFilterChange={handleFilterChange}
        selectedCopago={localCopago}
        onCopagoFilter={onCopagoFilter}
        onExport={hasPermission("VISUALIZAR_CC") ? onExport : undefined}
        hasPermission={hasPermission}
        selectedEps={selectedEps}
        setSelectedEps={setSelectedEps}
        isLoading={isLoading}
      />

      {openDialog && hasPermission("PROGRAMAR_CC") && (
        <TrasladoDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onSubmit={() => {
            handleProgramarTraslado();
            // Usar refresh automático después de programar traslado
            setTimeout(() => refreshTraslados(), 1000);
          }}
          isTai={false} // Solo mostrar autorizaciones normales (no TAI)
        />
      )}

      <div className="traslados-list" style={{ marginTop: 16 }}>
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
            <Typography.Text style={{ fontSize: "16px" }}>
              Cargando traslados, por favor espere...
            </Typography.Text>
          </div>
        ) : trasladosFiltrados.length > 0 ? (
          <>
          {puedeActualizarOrigenDestino && (
            <SeleccionVisibles
              visibles={trasladosPaginados}
              seleccionados={seleccionados}
              setSeleccionados={setSeleccionados}
            />
          )}
          {trasladosPaginados.map((traslado) => (
            <ItemTraslado
              key={traslado.id}
              traslado={{
                ...traslado,
                distancia_estimada: Number(traslado.distancia_estimada),
                copago: traslado.copago ? Number(traslado.copago) : 0,
                observaciones_count: traslado.observaciones_count || 0,
              }}
              handleContextMenu={handleContextMenu}
              regimenes={regimenes}
              hasPermission={hasPermission}
              seleccionable={puedeActualizarOrigenDestino}
              seleccionado={seleccionados.includes(traslado.id)}
              onToggleSeleccion={toggleSeleccion}
            />
          ))}
          </>
        ) : (
          <Typography.Text>No hay traslados disponibles.</Typography.Text>
        )}

        {/* Botón de exportación - Añadir esto en tu sección de filtros */}
        {hasPermission("VISUALIZAR_CC") && (
          <Modal
            title="Exportar Traslados"
            open={exportModalVisible}
            onCancel={() => setExportModalVisible(false)}
            footer={[
              <Button key="cancel" onClick={() => setExportModalVisible(false)}>
                Cancelar
              </Button>,
              <Button
                key="export"
                type="primary"
                loading={exportLoading}
                onClick={handleExportTraslados}
                icon={<DownloadOutlined />}
              >
                Exportar
              </Button>,
            ]}
          >
            <div style={{ marginBottom: 16 }}>
              <p>Seleccione el rango de fechas:</p>
              <p style={{ color: "#8c8c8c", fontSize: 14, marginTop: -10 }}>
                El rango máximo permitido es de 3 meses
              </p>
            </div>

            <RangePicker
              style={{ width: "100%" }}
              onChange={setExportDateRange}
              disabledDate={(current) => {
                if (!exportDateRange || exportDateRange.length === 0) {
                  return false;
                }
                const diffInMonths = dayjs(exportDateRange[1]).diff(
                  dayjs(exportDateRange[0]),
                  "month"
                );
                return diffInMonths > 3;
              }}
            />
          </Modal>
        )}
      </div>

      <PaginacionTrasladosAnt
        // Usamos la longitud de los traslados filtrados para la paginación
        count={trasladosFiltrados.length}
        totalAbsoluto={totalAbsoluto}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />

      {selectedTraslado && (
        <MenuContextualTraslado
          contextMenu={contextMenu}
          handleCloseContextMenu={handleCloseContextMenu}
          handleOptionClick={handleOptionClick}
          trasladoId={selectedTraslado.id}
          traslado={selectedTraslado}
          origin={geoJsonToLatLng(selectedTraslado.origen_coordenadas)}
          destination={geoJsonToLatLng(selectedTraslado.destino_coordenadas)}
          onEstadoCambiado={handleEstadoCambiado}
          hasPermission={hasPermission}
          seleccionados={seleccionados}
          onOrigenDestinoActualizado={handleOrigenDestinoActualizado}
        />
      )}
    </>
  );
}

// Casilla general: selecciona o quita los traslados visibles (página actual) sin tocar el resto de la selección
function SeleccionVisibles({ visibles, seleccionados, setSeleccionados }) {
  const idsVisibles = visibles.map((t) => t.id);
  const marcados = idsVisibles.filter((id) => seleccionados.includes(id)).length;
  const todos = idsVisibles.length > 0 && marcados === idsVisibles.length;
  const alternar = (e) => {
    setSeleccionados((prev) =>
      e.target.checked
        ? Array.from(new Set([...prev, ...idsVisibles]))
        : prev.filter((id) => !idsVisibles.includes(id))
    );
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
      <Checkbox checked={todos} indeterminate={marcados > 0 && !todos} onChange={alternar}>
        Seleccionar visibles ({idsVisibles.length})
      </Checkbox>
      {seleccionados.length > 0 && (
        <>
          <Typography.Text type="secondary">
            {seleccionados.length} seleccionado(s): clic derecho en uno de ellos para actualizar origen - destino
          </Typography.Text>
          <Button size="small" type="link" onClick={() => setSeleccionados([])}>
            Limpiar selección
          </Button>
        </>
      )}
    </div>
  );
}

SeleccionVisibles.propTypes = {
  visibles: PropTypes.arrayOf(PropTypes.object).isRequired,
  seleccionados: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSeleccionados: PropTypes.func.isRequired,
};

ListaTraslados.propTypes = {
  openDialog: PropTypes.bool.isRequired,
  handleOpenDialog: PropTypes.func.isRequired,
  handleCloseDialog: PropTypes.func.isRequired,
  handleProgramarTraslado: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  selectedZona: PropTypes.string.isRequired,
  setSelectedZona: PropTypes.func.isRequired,
  selectedStatuses: PropTypes.array.isRequired,
  setSelectedStatuses: PropTypes.func.isRequired,
  startDate: PropTypes.instanceOf(Date),
  setStartDate: PropTypes.func.isRequired,
  endDate: PropTypes.instanceOf(Date),
  setEndDate: PropTypes.func.isRequired,
  startTime: PropTypes.instanceOf(Date),
  setStartTime: PropTypes.func.isRequired,
  endTime: PropTypes.instanceOf(Date),
  setEndTime: PropTypes.func.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  selectedCopago: PropTypes.array,
  onCopagoFilter: PropTypes.func,
  onExport: PropTypes.func.isRequired,
  exportModalVisible: PropTypes.bool.isRequired,
  setExportModalVisible: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool.isRequired,
  exportDateRange: PropTypes.array,
  setExportDateRange: PropTypes.func.isRequired,
  handleExportTraslados: PropTypes.func.isRequired,
  regimenes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedEps: PropTypes.string,
  setSelectedEps: PropTypes.func,
};

export default ListaTraslados;
